/**
 * useOutreachHeldEvents — the held/cap-deferred notices for this campaign,
 * collected while `enabled` (Ruling 29: a cap deferral stays `queued`, so the
 * operation outcome says "applied" and only this event tells it apart).
 * useOutreachSSE keeps doing the cache invalidation for the same event.
 */
import { useCallback, useState } from 'react';
import { useSSE } from '@discovr/common';

type OutreachHeldPayload = {
  campaign_id?: string;
  entry_id?: string;
  message_id?: number;
  reason?: string;
  /** The pre-2.7 payload's name for `reason`. */
  held_reason?: string;
  scheduled_for?: string;
};

export interface OutreachHeldEvent {
  messageId: number;
  entryId?: string;
  reason: string;
  scheduledFor?: string;
}

export function useOutreachHeldEvents(campaignId: string, enabled: boolean): OutreachHeldEvent[] {
  const [events, setEvents] = useState<OutreachHeldEvent[]>([]);
  const onHeld = useCallback(
    (payload: OutreachHeldPayload) => {
      const messageId = payload.message_id;
      if (payload.campaign_id !== campaignId || typeof messageId !== 'number') return;
      const next: OutreachHeldEvent = {
        messageId,
        entryId: payload.entry_id,
        reason: payload.reason ?? payload.held_reason ?? '',
        scheduledFor: payload.scheduled_for,
      };
      setEvents((prev) => [...prev.filter((e) => e.messageId !== messageId), next]);
    },
    [campaignId],
  );
  useSSE('outreach_message_held', enabled && !!campaignId, onHeld);
  return events;
}
